import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import type { PaperNavHistoryEntry } from '@ricoh-paper/angular/shell';
import {
  COMMUNITY_ROOT_ROUTE,
  communityIdFromUrl,
  communityViewIdFromUrl,
  findWorkspace,
  isViewActive,
  personIdFromUrl,
  resolveSection,
  viewsForUrl,
  workspaceRoute,
} from '../constants/navigation';

/**
 * One frame of the back-stack, before its label is resolved.
 *
 * The LABEL is deliberately not stored. A frame's human name can depend on
 * data that has not arrived yet (a community's name on a cold link) or on the
 * active locale, and a string frozen at push time is wrong in both cases.
 * `entries()` resolves it at read time instead — see `setLabelResolver`.
 */
export interface NavHistoryFrame {
  /** Router-form URL, exactly as `NavigationEnd.urlAfterRedirects` gave it. */
  url: string;
  workspaceId: string;
  viewId: string | null;
  contextId: string | null;
  /** Epoch milliseconds the frame became current. */
  at: number;
}

/** What a navigation does to the stack. */
type StackOp = 'push' | 'pop' | 'replace';

/**
 * NavHistoryService — the app's ONE back model.
 *
 * RESPONSIBILITY
 *   Answer two questions and nothing else: *is there anywhere to go back to*,
 *   and *what is it called*. The movement itself is delegated, never owned.
 *
 * THE DECISION: BACK DELEGATES TO `Location.back()`
 *   A crumb that pops a stack of its own is a SECOND back affordance sitting
 *   beside the browser's, and two of them desynchronise within a couple of
 *   interactions — press the browser button once and the crumb is offering a
 *   destination the user has already left. So this service keeps no parallel
 *   history: `back()` calls `Location.back()`, the browser moves, and the
 *   stack here is a LABEL STORE that mirrors the movement rather than causing
 *   it.
 *
 *   It stays mirrored because Angular tells us which kind of navigation each
 *   one is — `NavigationStart.navigationTrigger` is `'popstate'` for a
 *   browser Back/Forward and `'imperative'` for a router call, and
 *   `Navigation.extras.replaceUrl` marks the ones that overwrite the current
 *   history entry instead of adding one (a community switching tabs). Push,
 *   pop, replace: three cases, applied to this stack exactly as the browser
 *   applies them to its own.
 *
 *   The consequence worth stating: this service never intercepts `popstate`
 *   and never calls `history.pushState`. There is one history; we annotate
 *   it.
 *
 * ANGULAR `Location` / `Router` ONLY — NEVER `window.history`
 *   `office-host.service.ts` captures `pushState` / `replaceState` before
 *   office.js loads and restores them afterwards, because office.js NULLS
 *   them in some Outlook webviews. On `#/addin/*` those methods can therefore
 *   be transiently null. Angular's `Location` is the only safe surface.
 *
 * ROUTER-FORM URLS
 *   The app runs `withHashLocation()`, and every existing store keeps
 *   router-form URLs (`Location.path()` strips the hash). Frames do the same.
 *   Push / FCM links arrive in either form and `core/utils/push-link.ts`
 *   normalises them before they reach the router, so nothing here has to.
 *
 * THE COLD-LINK CASE, EXPLICITLY
 *   A deep link opened from an email, a push notification or a QR code on
 *   signage starts with an EMPTY stack: the first `NavigationEnd` has no
 *   previous frame to push, so `entries()` is `[]`. That is not a gap to
 *   paper over — `paper-shell-bar` renders no back arrow without a stack, and
 *   that is right. Back retraces a path; on a cold link there is no path. The
 *   way out is UP (the context crumb's root row) and the workspace pill, both
 *   of which are always present inside a context.
 *
 * SIGNALS
 *   frames   — the stack behind the current entry, oldest first.
 *   entries  — the same, projected onto Paper's `PaperNavHistoryEntry`.
 *   current  — the frame the user is on. Null before the first navigation.
 *   canGoBack — whether `back()` will move.
 *
 * USED BY
 *   `features/shell/app-shell.component.ts` — the `[history]` input and the
 *   `(backRequested)` output of `paper-shell-bar`.
 *
 * BACKEND CONTRACT
 *   None. Pure client state.
 *
 * AI CONTEXT
 *   `ai-chat-page.component.ts` had the only `Location.back()` in `src/app`
 *   and the only warm/cold discrimination (`navigationId > 1` off
 *   `location.getState()`). This generalises it; that page keeps its own
 *   full-surface control because it suppresses the shell bar entirely.
 */
@Injectable({ providedIn: 'root' })
export class NavHistoryService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _frames = signal<readonly NavHistoryFrame[]>([]);
  private readonly _current = signal<NavHistoryFrame | null>(null);

  /**
   * Resolve a frame's human label. A signal rather than a field so
   * `entries()` recomputes the moment the shell registers one, and again
   * whenever the resolver's own dependencies (the community store, the
   * active locale) change.
   */
  private readonly _labelResolver = signal<
    ((frame: NavHistoryFrame) => string | null) | null
  >(null);

  /** What is BEHIND the current entry, oldest first. */
  readonly frames = this._frames.asReadonly();

  /** The frame the user is on, or null before the first navigation. */
  readonly current = this._current.asReadonly();

  /**
   * The stack projected onto Paper's contract. `paper-shell-bar` labels its
   * back affordance from the LAST entry and hides it entirely when the array
   * is empty, so this is both the data and the gate.
   */
  readonly entries = computed<readonly PaperNavHistoryEntry[]>(() => {
    const resolve = this._labelResolver();
    return this._frames().map(frame => ({
      workspaceId: frame.workspaceId,
      viewId: frame.viewId,
      contextId: frame.contextId,
      label: resolve?.(frame) ?? fallbackLabel(frame),
      url: frame.url,
      at: frame.at,
    }));
  });

  /** Whether `back()` has somewhere to go. */
  readonly canGoBack = computed(() => this._frames().length > 0);

  /**
   * A surface that owns its own way out — the article editor, whose Back
   * flushes a final save and can raise a "Leave without saving?" dialog.
   *
   * WHY A HOOK AND NOT A GUARD
   *   `CanDeactivate` would also catch the browser's own Back button, which
   *   sounds better until you notice it cannot run an async confirm without
   *   first cancelling the navigation and restoring the URL. The editor's
   *   exit flow is async by construction (it awaits a save). This hook covers
   *   every affordance THIS service owns — the crumb's arrow and `Alt + ←` —
   *   and hands the surface the decision rather than routing around it.
   */
  private exitGuard: (() => boolean) | null = null;

  constructor() {
    // What the in-flight navigation will do to the stack. Captured on
    // NavigationStart, where the trigger and the extras are still available,
    // and applied on NavigationEnd, where the post-redirect URL is.
    let pending: StackOp = 'push';

    // ONE subscription over both events, so the operation captured on Start
    // and the URL read on End cannot be interleaved by a second stream.
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart | NavigationEnd =>
            event instanceof NavigationStart || event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (event: NavigationStart | NavigationEnd) => {
          if (event instanceof NavigationStart) {
            // `restoredState` is non-null only when the browser restored a
            // history entry, which is the same condition as a 'popstate'
            // trigger — both are checked because a hashchange in the Tauri
            // webview reports the trigger without the state.
            if (event.navigationTrigger === 'popstate' || event.restoredState) {
              pending = 'pop';
              return;
            }
            // A 'hashchange' is the browser telling us the URL moved on its
            // own — a pasted or edited hash. It ADDS a history entry, so it
            // pushes. Its `extras.replaceUrl` is deliberately not consulted:
            // Angular sets it on every browser-sync navigation so the router
            // does not double-write the entry the browser already made, and
            // reading it here would mistake the commonest kind of push for a
            // replace. `replaceUrl` only means "replace" when the APP asked.
            pending =
              event.navigationTrigger === 'imperative'
                && this.router.getCurrentNavigation()?.extras?.replaceUrl
                ? 'replace'
                : 'push';
            return;
          }
          this.commit(event.urlAfterRedirects, pending);
          pending = 'push';
        },
        // The router's event stream does not error in practice; if it ever
        // does, the stack stops tracking and back stops being offered —
        // which is the safe direction, but it must not be silent.
        error: (err: unknown) =>
          console.warn('[NavHistoryService] router event stream error:', err),
      });
  }

  /**
   * Register the label resolver. The shell owns it: only the shell can name
   * a community or a colleague, and pulling those services in here would put
   * a feature dependency inside `core`.
   */
  setLabelResolver(resolve: (frame: NavHistoryFrame) => string | null): void {
    this._labelResolver.set(resolve);
  }

  /**
   * Register (or clear, with `null`) the current surface's own exit flow.
   * Returns a de-registration function so a component can wire it to its
   * `DestroyRef` and never leave a stale guard behind — a guard that
   * outlived its page would swallow back on every subsequent surface.
   */
  setExitGuard(guard: (() => boolean) | null): () => void {
    this.exitGuard = guard;
    return () => {
      if (this.exitGuard === guard) this.exitGuard = null;
    };
  }

  /**
   * BACK — retrace the actual path. Delegates to the browser, so the crumb's
   * arrow, `Alt + ←` and the browser's own button are ONE affordance.
   *
   * Returns false when it did not move: either the current surface handled
   * the exit itself, or there is no stack (a cold link — see the class docs).
   */
  back(): boolean {
    if (this.exitGuard?.()) return false;
    if (!this.canGoBack()) return false;
    this.location.back();
    return true;
  }

  /**
   * UP — the root of the surface you are in, regardless of how you got here.
   * Never the same control as back (handover §2.2): back retraces, up
   * ascends, and on a cold link up is the only one of the two that exists.
   */
  up(url = this.router.url): boolean {
    const target = upTarget(url);
    if (!target) return false;
    void this.router.navigateByUrl(target);
    return true;
  }

  /** Apply one navigation to the stack. Exposed for the spec. */
  private commit(url: string, op: StackOp): void {
    const next = frameFor(url);
    const previous = this._current();

    if (op === 'pop') {
      // The browser has already moved. Drop the frame we came from; if the
      // user went back further than we ever recorded (a Forward into a
      // reloaded entry, say) the stack simply empties and back stops being
      // offered, which is the honest answer.
      this._frames.update(frames => frames.slice(0, -1));
    } else if (op === 'push' && previous && previous.url !== next.url) {
      this._frames.update(frames => [...frames, previous]);
    }
    // 'replace' overwrites the current history entry, so the stack is
    // unchanged and only `current` moves. A community switching tabs is a
    // view of the same place, not a new one — see `HpCommunityDetailComponent`.

    this._current.set(next);
  }
}

/** Build a frame from a router-form URL, using registry data alone. */
function frameFor(url: string): NavHistoryFrame {
  const workspaceId = resolveSection(url);
  const communityId = communityIdFromUrl(url);
  const personId = personIdFromUrl(url);
  const contextId = communityId ?? personId;
  return {
    url,
    workspaceId,
    contextId,
    viewId: communityId
      ? communityViewIdFromUrl(url)
      : (activeWorkspaceViewId(workspaceId, url) ?? null),
    at: Date.now(),
  };
}

/** The workspace view `url` sits in, from the registry. */
function activeWorkspaceViewId(workspaceId: string, url: string): string | null {
  const workspace = findWorkspace(workspaceId);
  if (!workspace) return null;
  return viewsForUrl(workspace, url).find(view => isViewActive(view, url))?.id ?? null;
}

/**
 * The label to show when nothing better is available — the workspace's
 * English name. The shell's resolver localises and, inside a context, names
 * the entity instead; this is what a bare-vitest spec and the frames before
 * the shell registers see.
 */
function fallbackLabel(frame: NavHistoryFrame): string {
  return findWorkspace(frame.workspaceId)?.label ?? frame.workspaceId;
}

/**
 * Where UP goes from `url`. A community ascends to the directory; anything
 * else ascends to its workspace root. Null when the URL is already a
 * workspace root, because a control that goes nowhere should not be offered.
 */
function upTarget(url: string): string | null {
  if (communityIdFromUrl(url)) return COMMUNITY_ROOT_ROUTE;
  const root = workspaceRoute(resolveSection(url));
  return url.split('?')[0] === root ? null : root;
}
